import { useEffect, useRef, useState } from 'react'
import {
  AddDynamicTableDataMutation,
  AddDynamicTableDataMutationVariables,
  CreateDynamicTableFilterMutation,
  CreateDynamicTableFilterMutationVariables,
  DeleteDynamicTableDataMutation,
  DeleteDynamicTableDataMutationVariables,
  DynamicTableFilters,
  DynamicTablePublicAccessInput,
  GetAllDynamicTableDataQuery,
  GetAllDynamicTableFilterQuery,
  RemoveDynamicTableFilterMutation,
  RemoveDynamicTableFilterMutationVariables,
  SetDynamicTablePublicAccessMutation,
  SetDynamicTablePublicAccessMutationVariables,
  useAddDynamicTableDataMutation,
  useCreateDynamicTableFilterMutation,
  useDeleteDynamicTableDataMutation,
  useGetAllDynamicTableDataQuery,
  useGetAllDynamicTableFilterQuery,
  useRemoveDynamicTableFilterMutation,
  useSetDynamicTablePublicAccessMutation,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import Toast, { ToastType } from '../../../../utils/toast'
import DynamicTablePageWrapper, {
  DynamicTableDataInputType,
  DynamicTableDataType,
  DynamicTableDataInput,
} from './DynamicTablesPageWrapper'
import { useLocation } from 'react-router'
import { RefetchUserContext } from '../../../../utils/RefetchUserContext'

export const DynamicTableMainWrapper = () => {
  const [tableData, setTableData] = useState<DynamicTableDataType[]>([])
  const [filterData, setFilterData] = useState<DynamicTableFilters[]>([])
  const [saveLoader, setSaveLoader] = useState(false)
  const [deleteLoader, setDeleteLoader] = useState(false)
  const [publishLoader, setPublishLoader] = useState(false)

  const location = useLocation()
  const publishedData: any = location.state || undefined

  const isSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true' ? true : false
  const [selectedTable, setSelectedTable] = useState<DynamicTableDataType>()
  const { data } = useGetAllDynamicTableDataQuery<GetAllDynamicTableDataQuery, Error>(
    graphqlRequestClient,
    {},
    {}
  )

  const { data: allFilterData } = useGetAllDynamicTableFilterQuery<
    GetAllDynamicTableFilterQuery,
    Error
  >(
    graphqlRequestClient,
    {
      input: { tableId: selectedTable?.id },
    },
    {
      enabled: !!selectedTable,
    }
  )

  const mutateSaveDynamicTableFilter = useCreateDynamicTableFilterMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: CreateDynamicTableFilterMutation,
        _variables: CreateDynamicTableFilterMutationVariables,
        _context: unknown
      ) => {
        queryClient.invalidateQueries(useGetAllDynamicTableFilterQuery.getKey())

        Toast('Filter Saved Successfully', ToastType.success)
      },
      onError: (error: any) => {
        Toast(error.response.errors[0].message, ToastType.error)
      },
    },
    {}
  )

  const mutatePublishDynamicTable = useSetDynamicTablePublicAccessMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: SetDynamicTablePublicAccessMutation,
        _variables: SetDynamicTablePublicAccessMutationVariables,
        _context: unknown
      ) => {
        queryClient.invalidateQueries(useGetAllDynamicTableFilterQuery.getKey())
        queryClient.invalidateQueries(useGetAllDynamicTableDataQuery.getKey())
        setPublishLoader(false)
        RefetchUserContext()
        Toast('Dataset visibility updated successfully', ToastType.success)
      },
      onError: (error: any) => {
        setPublishLoader(false)
        Toast(error.response.errors[0].message, ToastType.error)
      },
    },
    {}
  )

  const mutateDeleteDynamicTableFilter = useRemoveDynamicTableFilterMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: RemoveDynamicTableFilterMutation,
        _variables: RemoveDynamicTableFilterMutationVariables,
        _context: unknown
      ) => {
        queryClient.invalidateQueries(useGetAllDynamicTableFilterQuery.getKey())

        Toast('Filter Removed Successfully', ToastType.success)
      },
      onError: (error: any) => {
        Toast(error.response.errors[0].message, ToastType.error)
      },
    },
    {}
  )
  const mutateSaveDynamicTableData = useAddDynamicTableDataMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: AddDynamicTableDataMutation,
        _variables: AddDynamicTableDataMutationVariables,
        _context: unknown
      ) => {
        queryClient.invalidateQueries(useGetAllDynamicTableDataQuery.getKey())
        setSaveLoader(false)
        Toast('Data Saved Successfully', ToastType.success)
      },
      onError: (error: any) => {
        setSaveLoader(false)
        Toast(error.response.errors[0].message, ToastType.error)
      },
    },
    {}
  )

  const mutateRemoveCompany = useDeleteDynamicTableDataMutation<Error>(graphqlRequestClient, {
    onSuccess: (
      data: DeleteDynamicTableDataMutation,
      _variables: DeleteDynamicTableDataMutationVariables,
      _context: unknown
    ) => {
      queryClient.invalidateQueries(useGetAllDynamicTableDataQuery.getKey())
      RefetchUserContext()
      setDeleteLoader(false)
      Toast('Data Removed Successfully', ToastType.success)
    },
    onError: (error: any) => {
      setDeleteLoader(false)
      Toast(error.response.errors[0].message, ToastType.error)
    },
  })
  const deleteBtnRef = useRef<HTMLButtonElement | null>(null)

  function handleRemoveTableData(e: React.MouseEvent, currentId: number) {
    setDeleteLoader(true)
    e.stopPropagation()
    // Disable indicator after 3 seconds
    deleteBtnRef.current?.setAttribute('data-kt-indicator', 'on')
    setTimeout(() => {
      // Activate indicator
      deleteBtnRef.current?.removeAttribute('data-kt-indicator')
    }, 3000)
    e.preventDefault()
    mutateRemoveCompany.mutate({ input: { id: currentId } })
  }

  useEffect(() => {
    data &&
      data.getDynamicTableData &&
      setTableData(data.getDynamicTableData as DynamicTableDataType[])
  }, [data])

  useEffect(() => {
    allFilterData &&
      allFilterData.getAllDynamicTableFilter &&
      setFilterData(allFilterData.getAllDynamicTableFilter as DynamicTableFilters[])
  }, [allFilterData])

  function handleSave(values: any) {
    setSaveLoader(true)
    mutateSaveDynamicTableData.mutate({
      input: {
        file: values.file,
        tableData: values.tableData,
        chartData: values.chartData,
        description: values.description,
        name: values.name,
        isExisting: values.isExisting,
        isPublished: values.isPublished,
        id: values.id && Number(values.id),
      },
    })
  }

  function handleSaveFilter(values: any) {
    mutateSaveDynamicTableFilter.mutate({
      input: {
        tableId: values.tableId,
        filterName: values.filterName,
        filterState: values.filterState,
        tableName: values.tableName,
      },
    })
  }

  function handleRemoveFilter(values: any) {
    mutateDeleteDynamicTableFilter.mutate({
      input: {
        tableId: Number(values.tableId),
        tableName: values.tableName,
        filterName: values.filterName,
      },
    })
  }

  function handlePublishTable(values: any) {
    setPublishLoader(true)
    mutatePublishDynamicTable.mutate({
      input: {
        id: Number(values.id),
        isPublic: values.isPublic || false,
      },
    })
  }

  return (
    <div>
      <div className='d-flex justify-content-start align-items-center my-4'>
        <div className='d-flex flex-column'>
          <div className='d-flex align-items-baseline border-4 border-start border-primary ps-4'>
            <h2 className='mb-0 fw-bold my-1'>Datasets</h2>
          </div>
        </div>
      </div>
      <DynamicTablePageWrapper
        data={tableData}
        isSuperAdmin={isSuperAdmin}
        publishedTable={publishedData}
        handleSave={(values: DynamicTableDataInput) => {
          handleSave(values)
        }}
        handleRemove={(event: React.MouseEvent, id: number) => {
          handleRemoveTableData(event, id)
        }}
        onSelectTable={(table: any) => {
          setSelectedTable(table)
        }}
        filterData={filterData}
        saveFilter={(values: any) => {
          handleSaveFilter(values)
        }}
        removeFilter={(values: any) => {
          handleRemoveFilter(values)
        }}
        publishTable={(values: DynamicTablePublicAccessInput) => {
          handlePublishTable(values)
        }}
        loaders={{
          isSaveLoader: saveLoader,
          isDeleteLoader: deleteLoader,
          isPublishLoader: publishLoader,
        }}
      />

      {/* <DynamicAGTable
        data={tableData}
        handleSave={(values: DynamicTableDataInput) => {
          handleSave(values)
        }}
        handleRemove={(event: React.MouseEvent, id: number) => {
          handleRemoveTableData(event, id)
        }}
        onSelectTable={(table: any) => {
          setSelectedTable(table)
        }}
        filterData={filterData}
        saveFilter={(values: any) => {
          handleSaveFilter(values)
        }}
        removeFilter={(values: any) => {
          handleRemoveFilter(values)
        }}
      /> */}
    </div>
  )
}
